import * as React from 'react';
import { styled } from '@glitz/react';
import { Button } from 'webapp/components/button';
import { absoluteCenterDecorator } from 'webapp/decorators/center';
import { Theme, isSvenssons } from 'webapp/theme';
import { SiteLogo } from 'webapp/shared/logos';
import {
  ROOT_KEY,
  findItem,
  findParentKey,
  useInitialMenusItemByUrl,
  type ItemKeyType,
} from 'webapp/shell/main-menu/helpers';
import { IconLink } from 'webapp/shell/main-menu/IconLink';
import Nav from './Nav';
import { Tabs, TabsType } from 'webapp/shell/main-menu/Tabs';
import { CloseIcon } from 'webapp/components/icons';
import { Promo } from 'webapp/shell/main-menu/Promo';
import { MobileMenuBottomBar as GeneralBottomBar } from 'nordicnest/shell/main-menu/MobileMenuBottomBar';
import { MobileMenuBottomBar as SvenssonsBottomBar } from 'svenssons/shell/main-menu/MobileMenuBottomBar';
import { addNavigationEventToDataLayer } from 'webapp/tracking/events/main-navigation';

type PropType = {
  menu: NordicNest.Api.CfMenuApiModel;
  close: () => void;
  isOpen: boolean;
};

function MobileMenu({ menu, close, isOpen }: PropType) {
  const [visibleKey, setLevel] = useInitialMenusItemByUrl(isOpen);
  const menuItems = React.useMemo(() => menu.primaryLinks ?? [], [menu.primaryLinks]);
  const secondaryItems = React.useMemo(() => menu.secondaryMobileLinks ?? [], [menu.secondaryMobileLinks]);
  const [secondaryVisibleKey, setSecondaryVisibleKey] = React.useState<ItemKeyType>();
  const [selectedTab, setSelectedTab] = React.useState(TabsType.Products);

  const setSecondaryLevel = React.useCallback(
    (nextVisibleKey: ItemKeyType) => {
      if (secondaryVisibleKey !== nextVisibleKey) {
        setSecondaryVisibleKey(nextVisibleKey);
      }
    },
    [secondaryVisibleKey],
  );
  const popLevel = React.useCallback(() => {
    selectedTab === TabsType.Products
      ? setLevel(findParentKey(menuItems, visibleKey))
      : setSecondaryVisibleKey(findParentKey(secondaryItems, secondaryVisibleKey));
  }, [menuItems, secondaryItems, secondaryVisibleKey, selectedTab, setLevel, visibleKey]);

  let currentItem = findItem(menuItems, visibleKey);
  const lastChild = Boolean(currentItem?.childPages?.length === 0);

  let currentSecondaryItem = findItem(secondaryItems, secondaryVisibleKey);
  const lastSecondaryChild = Boolean(currentSecondaryItem?.childPages?.length === 0);

  if (lastChild && currentItem) {
    currentItem = findItem(menuItems, currentItem.parentUrl);
  }

  if (lastSecondaryChild && currentSecondaryItem) {
    currentSecondaryItem = findItem(secondaryItems, currentSecondaryItem.parentUrl);
  }
  const isRoot = visibleKey === ROOT_KEY || !currentItem;
  const isSecondaryRoot = secondaryVisibleKey === ROOT_KEY || !currentSecondaryItem;

  const switchTab = (i: number) => {
    setSelectedTab(i);
    if (i === 0) setSecondaryVisibleKey(undefined);
  };

  const promoAndSecondaryToplinks = (
    <>
      {menu?.promo && (
        <Promo
          promo={menu?.promo}
          onClick={() => {
            close();
            addNavigationEventToDataLayer({ to: menu?.promo?.path, menu_type: 'mobile', menu_category: 'promo' });
          }}
        />
      )}
      {menu?.secondaryTopLinks && !isSvenssons && (
        <GridPages>
          {menu.secondaryTopLinks.map(link => (
            <IconLink
              menuItem={link}
              key={link.link.to}
              navigationData={{ menu_type: 'mobile', menu_category: 'bottom' }}
            />
          ))}
        </GridPages>
      )}
    </>
  );

  const secondaryMenuItems = (
    <Nav
      key={selectedTab}
      current={currentSecondaryItem ? currentSecondaryItem : secondaryItems}
      setLevel={setSecondaryLevel}
      selectedTab={selectedTab}
    >
      {isSecondaryRoot && promoAndSecondaryToplinks}
    </Nav>
  );

  const BottomBar = isSvenssons ? SvenssonsBottomBar : GeneralBottomBar;

  return (
    <>
      {!isSvenssons && (
        <Top>
          <Logo />
          <CloseButton onClick={close} square as={Theme.Transparent}>
            <CloseIcon />
          </CloseButton>
        </Top>
      )}
      <Tabs
        isRootLevel={isSecondaryRoot}
        isProductRootLevel={isRoot}
        popLevel={popLevel}
        selectedTab={selectedTab}
        onClick={i => switchTab(i)}
      />
      <Base>
        {selectedTab === TabsType.Products ? (
          <Nav
            key={selectedTab}
            current={currentItem ? currentItem : menuItems}
            setLevel={setLevel}
            selectedTab={selectedTab}
          >
            {isRoot && promoAndSecondaryToplinks}
          </Nav>
        ) : (
          secondaryMenuItems
        )}
        {isSecondaryRoot && !__BROWSER__ && secondaryMenuItems}
      </Base>
      <BottomBar close={close} />
    </>
  );
}

const Base = styled.nav({
  flexGrow: 1,
  position: 'relative',
  overflowX: 'hidden',
  height: '100%',
});

const Logo = styled(SiteLogo, absoluteCenterDecorator);

const CloseButton = styled(Button, {
  float: 'right',
});

const Top = styled.div({
  position: 'relative',
  display: 'block',
  padding: { y: t => t.sharedSpacing.xxs },
});

const GridPages = styled.li({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  width: '100%',
  padding: { xy: t => t.spacing.xs },
  fontSize: t => t.fontSize.xxs,
});

export default React.memo(MobileMenu);
